import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from 'src/components/ui/sheet';
import { Company } from 'src/pages/billing/types/billing.types';
import { Invoice, Terms, ValidationMismatchEntry } from 'src/redux/billing/billing.types';
import { SKU } from 'src/redux/billing/billing.types';
import InvoiceDrawerHeader from './invoice-drawer-header';
import InvoiceDrawerLineTable from './invoice-drawer-line-table';
import { isInvoiceLocked } from '../../utils/billing-utils';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/redux-store';

interface InvoiceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice?: Invoice | null; // null for create mode, string for edit mode
  invoices: Invoice[];
  companies?: Company[];
  skus?: SKU[];
  /** Full product catalogue (incl. archived) for SKU Helper resolution */
  skuCatalogue?: SKU[];
  terms?: Terms[];
  title?: string;
  updateInvoice: (invoiceId: string, field: string, value: any) => void;
  updateLineItem: (lineItemId: string, field: string, value: any) => Promise<void>;
  refetchInvoiceGroup: () => void;
  /** Approve all pending line items for the chosen platform (invoices tab drawer). */
  bulkApproveInvoiceLinesByPlatform?: (platformKey: string) => Promise<void>;
  /** From invoice_group.validations.mismatches; used for attention-needed validation mismatch UI */
  validationMismatches?: ValidationMismatchEntry[];
  isLoadingLineItems?: boolean;
}

export const InvoiceDrawer: React.FC<InvoiceDrawerProps> = ({
  open,
  onOpenChange,
  invoice,
  invoices,
  companies,
  skus,
  skuCatalogue,
  terms,
  title,
  updateInvoice,
  updateLineItem,
  refetchInvoiceGroup,
  validationMismatches,
  bulkApproveInvoiceLinesByPlatform,
  isLoadingLineItems = false,
}) => {
  const userState = useSelector((state: RootState) => state.userState);
  const isLocked =
    invoice != null
      ? isInvoiceLocked(invoice, userState.permissions?.groups || [])
      : false;

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="!w-[75vw] sm:!w-[85vw] !max-w-none bg-white flex flex-col p-0"
        // onInteractOutside={(e) => {
        //   e.preventDefault();
        // }}
      >
        <SheetTitle className="sr-only">
          {invoice?.ext_invoice_number
            ? `Invoice ${invoice.ext_invoice_number}`
            : 'Invoice'}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Edit invoice details, line items, and billing options for this invoice.
        </SheetDescription>
        <div className="flex-1 overflow-y-auto p-6 pt-3">
          <InvoiceDrawerHeader
            isLocked={isLocked}
            invoice={invoice}
            companies={companies}
            terms={terms}
            skus={skus}
            refetchInvoiceGroup={refetchInvoiceGroup}
            updateInvoice={updateInvoice}
            bulkApproveInvoiceLinesByPlatform={bulkApproveInvoiceLinesByPlatform}
          />

          <div className="mt-6">
            <InvoiceDrawerLineTable
              isLocked={isLocked}
              invoice={invoice}
              skus={skus}
              skuCatalogue={skuCatalogue}
              companies={companies || []}
              invoices={invoices}
              updateLineItem={updateLineItem}
              refetchInvoiceGroup={refetchInvoiceGroup}
              validationMismatches={validationMismatches}
              isLoadingLineItems={isLoadingLineItems}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InvoiceDrawer;